import React, { useState, useEffect } from 'react';
import { Phone, Mail, Clock, Lock, Eye, EyeOff, CheckCircle, MessageSquare, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminInquiries = () => {
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [inquiries, setInquiries] = useState([]);
  const [activeTab, setActiveTab] = useState('new');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchInquiries = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/contact/inquiries?password=${encodeURIComponent(password)}`);
      setInquiries(res.data);
    } catch (err) {
      setError('Failed to fetch inquiries. Check your password.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await axios.get(`${API}/contact/inquiries?password=${encodeURIComponent(password)}`);
      setIsAuthenticated(true);
      fetchInquiries();
    } catch (err) {
      setError('Invalid password');
      setLoading(false);
    }
  };

  const handleStatusChange = async (inquiryId, newStatus) => {
    try {
      await axios.put(`${API}/contact/inquiries/${inquiryId}/status?status=${newStatus}&password=${encodeURIComponent(password)}`);
      fetchInquiries();
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchInquiries();
    }
  }, [isAuthenticated]);

  const serviceLabels = {
    "bed-bugs": "Bed Bug Control",
    "rodents": "Rodent Control",
    "insects": "Insect Control",
    "wasps": "Wasp Treatment",
    "birds": "Bird Control & Deterrents",
    "commercial": "Commercial Services",
    "other": "Other / Not Sure"
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredInquiries = inquiries.filter(i => i.status === activeTab);

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-4">
        <div className="bg-white border border-neutral-200 rounded-2xl p-8 w-full max-w-md">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-brand/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-brand" />
            </div>
            <h1 className="text-2xl font-bold text-neutral-900">Admin Login</h1>
            <p className="text-neutral-600">Enter password to manage enquiries</p>
          </div>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter admin password"
                className="bg-white border-neutral-300 pr-10"
                data-testid="admin-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            
            {error && (
              <p className="text-red-500 text-sm text-center">{error}</p>
            )}
            
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-brand hover:bg-brand-dark text-white"
              data-testid="admin-login-btn"
            >
              {loading ? 'Logging in...' : 'Login'}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 p-4 sm:p-8">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-2xl font-bold text-neutral-900">Customer Enquiries</h1>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => window.location.href = '/admin/reviews'}
              className="border-neutral-300"
            >
              Manage Reviews
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setIsAuthenticated(false);
                setPassword('');
              }}
              className="border-neutral-300"
            >
              Logout
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          <Button
            variant={activeTab === 'new' ? 'default' : 'outline'}
            onClick={() => setActiveTab('new')}
            className={activeTab === 'new' ? 'bg-brand hover:bg-brand-dark' : 'border-neutral-300'}
          >
            <AlertCircle className="w-4 h-4 mr-2" />
            New ({inquiries.filter(i => i.status === 'new').length})
          </Button>
          <Button
            variant={activeTab === 'contacted' ? 'default' : 'outline'}
            onClick={() => setActiveTab('contacted')}
            className={activeTab === 'contacted' ? 'bg-brand hover:bg-brand-dark' : 'border-neutral-300'}
          >
            <Phone className="w-4 h-4 mr-2" />
            Contacted ({inquiries.filter(i => i.status === 'contacted').length})
          </Button>
          <Button
            variant={activeTab === 'resolved' ? 'default' : 'outline'}
            onClick={() => setActiveTab('resolved')}
            className={activeTab === 'resolved' ? 'bg-brand hover:bg-brand-dark' : 'border-neutral-300'}
          >
            <CheckCircle className="w-4 h-4 mr-2" />
            Resolved ({inquiries.filter(i => i.status === 'resolved').length})
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-2 border-brand border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : (
          <div className="space-y-4">
            {filteredInquiries.length === 0 ? (
              <div className="bg-white border border-neutral-200 rounded-xl p-8 text-center">
                <p className="text-neutral-500">
                  No {activeTab} enquiries
                </p>
              </div>
            ) : (
              filteredInquiries.map((inquiry) => (
                <div
                  key={inquiry.id}
                  className="bg-white border border-neutral-200 rounded-xl p-6"
                  data-testid={`inquiry-card-${inquiry.id}`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                    <div className="flex-1">
                      {/* Header */}
                      <div className="flex items-center gap-2 mb-3 flex-wrap">
                        <span className="font-semibold text-lg text-neutral-900">{inquiry.name}</span>
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-brand/20 text-brand">
                          {serviceLabels[inquiry.service] || inquiry.service}
                        </span>
                      </div>
                      
                      {/* Contact Info */}
                      <div className="space-y-2 mb-4">
                        <a
                          href={`tel:${inquiry.phone}`}
                          className="flex items-center gap-2 text-neutral-700 hover:text-brand transition-colors"
                        >
                          <Phone className="w-4 h-4 text-brand" />
                          <span>{inquiry.phone}</span>
                        </a>
                        <a
                          href={`mailto:${inquiry.email}`}
                          className="flex items-center gap-2 text-neutral-700 hover:text-brand transition-colors"
                        >
                          <Mail className="w-4 h-4 text-brand" />
                          <span>{inquiry.email}</span>
                        </a>
                        <div className="flex items-center gap-2 text-neutral-500 text-sm">
                          <Clock className="w-4 h-4" />
                          <span>{formatDate(inquiry.created_at)}</span>
                        </div>
                      </div>
                      
                      {/* Message */}
                      {inquiry.message && (
                        <div className="bg-neutral-50 rounded-lg p-4 border-l-4 border-brand">
                          <div className="flex items-start gap-2">
                            <MessageSquare className="w-4 h-4 text-brand mt-1 flex-shrink-0" />
                            <p className="text-neutral-700">{inquiry.message}</p>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Actions */}
                    <div className="flex lg:flex-col gap-2">
                      {activeTab === 'new' && (
                        <Button
                          size="sm"
                          onClick={() => handleStatusChange(inquiry.id, 'contacted')}
                          className="bg-blue-500 hover:bg-blue-600 text-white"
                        >
                          <Phone className="w-4 h-4 mr-1" />
                          Mark Contacted
                        </Button>
                      )}
                      {activeTab === 'contacted' && (
                        <Button
                          size="sm"
                          onClick={() => handleStatusChange(inquiry.id, 'resolved')}
                          className="bg-green-500 hover:bg-green-600 text-white"
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Mark Resolved
                        </Button>
                      )}
                      {activeTab !== 'new' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleStatusChange(inquiry.id, 'new')}
                          className="border-neutral-300"
                        >
                          Move to New
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminInquiries;
